import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useRef,
  useState,
} from "react";

import type { Editor } from "@tiptap/core";
import type { EditorState } from "@tiptap/pm/state";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import {
  CheckIcon,
  LinkIcon,
  PencilIcon,
  UnlinkIcon,
  XIcon,
} from "lucide-react";

import { Button, ButtonGroup } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";

import { useEditorContext } from "../editor/EditorContext";

/** True when the selection is a non-empty text range (not an atom/node). */
const hasLinkableTextSelection = (state: EditorState) => {
  const { selection } = state;
  if (selection instanceof NodeSelection || selection.empty) {
    return false;
  }
  if (!(selection instanceof TextSelection)) {
    return false;
  }
  return (
    state.doc.textBetween(selection.from, selection.to, " ").trim().length > 0
  );
};

const shouldShowLinkToolbar = (editor: Editor, state: EditorState) => {
  if (editor.isDestroyed || !editor.isEditable) {
    return false;
  }
  return editor.isActive("link") || hasLinkableTextSelection(state);
};

/**
 * Selection bubble for adding / editing / removing links.
 * Uses TipTap BubbleMenu for positioning; UI matches the previous toolbar:
 * - selection → link button
 * - edit → URL input + accept / cancel
 * - existing link → URL label + edit / unlink
 */
export const FloatingLinkToolbar = () => {
  const { editor, commands } = useEditorContext();
  // Non-null while the URL field is open. Cleared by cancel / submit / hide.
  const [draft, setDraft] = useState<string | null>(null);
  const isEditingRef = useRef(false);

  const linkState = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      if (!instance || instance.isDestroyed) {
        return { href: "", empty: true, canShow: false };
      }
      const { empty } = instance.state.selection;
      const href = (instance.getAttributes("link").href as string) || "";
      return {
        href,
        empty,
        canShow: shouldShowLinkToolbar(instance, instance.state),
      };
    },
  });

  const href = linkState?.href ?? "";
  const empty = linkState?.empty ?? true;
  const canShow = linkState?.canShow ?? false;

  // Exit edit mode when the selection collapses away from a link.
  if (draft !== null && !canShow) {
    setDraft(null);
  }

  const isEditing = draft !== null && canShow;
  isEditingRef.current = isEditing;

  // Stable identity so Semantic UI Input only focuses on mount, not every keystroke.
  const focusInput = useCallback((node: HTMLInputElement | null) => {
    if (!node) {
      return;
    }
    node.focus({ preventScroll: true });
    node.select();
  }, []);

  const startEditing = () => {
    if (empty && href) {
      editor?.chain().focus().extendMarkRange("link").run();
    }
    setDraft(href);
  };

  const cancelEditing = () => {
    setDraft(null);
    editor?.commands.focus();
  };

  const submitHref = () => {
    const trimmed = (draft ?? "").trim();
    setDraft(null);
    if (!trimmed) {
      commands.removeLink?.();
      return;
    }
    commands.updateLink?.({ href: trimmed });
  };

  const removeLink = () => {
    commands.removeLink?.();
    setDraft(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitHref();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditing();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      editor={editor}
      updateDelay={0}
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        padding: 2,
        borderRadius: 6,
        zIndex: 11,
      }}
      options={{ placement: "bottom-start", offset: 8 }}
      shouldShow={({ editor: instance, state }) => {
        if (isEditingRef.current) {
          return true;
        }
        return shouldShowLinkToolbar(instance, state);
      }}
    >
      {isEditing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Input
            ref={focusInput}
            type="text"
            value={draft ?? ""}
            placeholder="Enter link..."
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            size="small"
            style={{ width: 450 }}
          />
          <ButtonGroup variant="outline" size="small">
            <Button
              type="button"
              aria-label="Apply link"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={submitHref}
            >
              <Icon icon={CheckIcon} />
            </Button>
            <Button
              type="button"
              aria-label="Cancel"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={cancelEditing}
            >
              <Icon icon={XIcon} />
            </Button>
          </ButtonGroup>
        </div>
      ) : href ? (
        <div style={{ position: "relative" }}>
          <div
            style={{
              display: "flex",
              position: "absolute",
              padding: "4px 8px",
              bottom: 34,
              left: 0,
              background: "#eaeaea",
              borderRadius: 4,
              alignItems: "center",
              fontSize: "0.75rem",
              lineHeight: "0.85rem",
              wordBreak: "break-all",
              maxWidth: 350,
              width: "max-content",
              gap: 4,
            }}
          >
            <Icon
              icon={LinkIcon}
              size="small"
              style={{ top: 0, position: "relative", width: 10 }}
            />
            {href}
          </div>
          <ButtonGroup size="small">
            <Button
              type="button"
              aria-label="Edit link"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={startEditing}
              variant="outline"
            >
              <Icon icon={PencilIcon} />
            </Button>
            <Button
              type="button"
              aria-label="Remove link"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={removeLink}
              variant="outline"
            >
              <Icon icon={UnlinkIcon} />
            </Button>
          </ButtonGroup>
        </div>
      ) : (
        <ButtonGroup size="small">
          <Button
            type="button"
            aria-label="Add link"
            onMouseDown={(e: MouseEvent) => e.preventDefault()}
            onClick={startEditing}
            variant="outline"
          >
            <Icon icon={LinkIcon} />
          </Button>
        </ButtonGroup>
      )}
    </BubbleMenu>
  );
};
